import {AfterContentInit, Component, EventEmitter, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ApInstrumentsEditComponent
}                                                                                from '../../../entry-components/ap-basedata-entry-components/ap-instruments-edit.component';
import {ApGridPagerType, IGridPagerData}                                         from '../../../ap-interface';
import {
  ApDynGridStatusColumnConfig
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridPagerConfig,
  IDynGridWizardChoiceData
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  ApDynComponentComponent
}                                                                                from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  InstrumentStore
}                                                                                from '../../../stores/docu/instrument.store';
import {filter, map}                                                             from 'rxjs/operators';
import {
  FormStore
}                                                                                from '../../../stores/layout/form.store';
import {IInstrumentGridData, InstrumentsPagerAction}                             from '../../types/instruments.type';
import {combineLatest, Observable, Subscription}                                 from 'rxjs';
import {ObjectFactory}                                                           from 'ts-tooling';
import {
  MachineStore
}                                                                                from '../../../stores/docu/machine.store';
import {
  ResourcesDataService
}                                                                                from '../../../services/data/resources/resources-data.service';
import {
  ApDateService
}                                                                                from '../../../ap-core/services/ap-date-service';
import {
  LoginStore
}                                                                                from '../../../stores/login/login.store';
import {
  ApDynGridEditColumnConfig
}                                                                                from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApTranslationService
}                                                                                from '../../../ap-utils/service/ap-translation.service';
import {
  nameOf
}                                                                                from '../../../ap-core/utils/ap-name-of';
import {
  IGridStatus
}                                                                                from '../../../ap-interface/interfaces/ap-grid-status.interface';
import {
  ApUtilService
}                                                                                from '../../../ap-utils/service/ap-util.service';
import {
  ApColumnState
}                                                                                from '../../../ap-interface/enums/ap-column-state.enum';
import IInstrument = Data.DocuContext.Instrument.IInstrument;

/**
 * Component for the instrument overview.
 */
@Component({
  selector: 'ap-instruments-overview',
  template: `
    <ap-dyncomponent [columns]="columns"
                     [items]="items$ | async"
                     [pager]="pager"
                     [loading$]="loading$">
    </ap-dyncomponent>`
})
export class ApInstrumentsComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public loading$: Observable<boolean>;
  public items$: Observable<IInstrumentGridData[]>;
  public columns: ApDynGridColumnConfigBase[];
  public pager: ApDynGridPagerConfig;

  private _applyClicked = new EventEmitter<any>();
  private _subscriptions: Subscription[] = [];

  constructor(private instrumentStore: InstrumentStore,
              private machineStore: MachineStore,
              private formStore: FormStore,
              private loginStore: LoginStore,
              private dateService: ApDateService,
              private resourcesService: ResourcesDataService,
              private translationService: ApTranslationService) {
  }

  /**
   * when the component is loaded
   */
  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.machineStore.Loading$,
      this.instrumentStore.Loading$
    ]).pipe(
      map(loadings => loadings.some(loading => loading))
    );
    this.gridBuilder();
    this.generatePager();
    this.setItems();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(this._applyClicked.subscribe((e) => this.onPagerApplyClicked(e)));
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s?.unsubscribe());
  }

  private setItems(): void {
    this.items$ = this.instrumentStore.Instruments$.pipe(
      filter(instruments => !!instruments),
      map(instruments => instruments.map((instrument: IInstrument) => {
        const status = this.resourcesService.getResourceStatus(instrument);
        const defaultWorktype = !!instrument.DefaultWorktype ? instrument.DefaultWorktype.Description : '';
        return {
          Id: instrument.Id,
          Status: status,
          Name: instrument.Name,
          DefaultWidth: instrument.DefaultWidth,
          DefaultWorkTypeName: this.translationService.translate(defaultWorktype),
          SourceItem: instrument
        } as IInstrumentGridData;
      }))
    );
  }

  /**
   * binding of validators
   */
  private gridBuilder(): void {
    this.columns = [
      new ApDynGridStatusColumnConfig({
        field: `${nameOf<IInstrumentGridData>('Status')}.${nameOf<IGridStatus>('state')}`,
        class: `${nameOf<IInstrumentGridData>('Status')}.${nameOf<IGridStatus>('class')}`,
        description: `${nameOf<IInstrumentGridData>('Status')}.${nameOf<IGridStatus>('description')}`,
        title: 'Global__Status',
        groupTitle: 'Global__Status',
        showHeaderTitle: false,
        filterable: true,
        sortIndex: 0,
        sortDesc: true,
        groupIndex: 0,
        groupDesc: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IInstrumentGridData>('Name'),
        title: 'Docu_Ini__Description',
        sortIndex: 1,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IInstrumentGridData>('DefaultWidth'),
        title: 'Docu_Ini__WorkingWidth',
        filterable: true,
        headerFilterable: true,
        cssClass: 'right',
        filterType: FilterType.NUMBER
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IInstrumentGridData>('DefaultWorkTypeName'),
        title: 'Docu_Ini__WorkType',
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridEditColumnConfig({
        data: [
          {
            text: 'Global__Edit',
            callback: ApUtilService.createEventEmitter(this.onEdit.bind(this), this._subscriptions)
          },
          {
            text: (dataItem: IInstrumentGridData) => dataItem.Status.state === ApColumnState.Green
              ? 'Global__Disable' : 'Global__Enable',
            callback: ApUtilService.createEventEmitter(this.onEnableDisableToggle.bind(this), this._subscriptions),
            disabled: (dataItem: IInstrumentGridData) => this.isPresentInMachines(dataItem)
          },
          {
            text: 'Global__Delete',
            callback: ApUtilService.createEventEmitter(this.onDelete.bind(this), this._subscriptions),
            disabled: (dataItem: IInstrumentGridData) => this.isPresentInMachines(dataItem)
          },
        ]
      })
    ];
  }

  /**
   * fill wizard choice data
   */
  private generatePager(): void {
    this.pager = new ApDynGridPagerConfig({
      listMassEditing: [
        {
          id: InstrumentsPagerAction.Active,
          name: this.translationService.translate('Global__Enable'),
          type: ApGridPagerType.None,
          values: null
        } as IDynGridWizardChoiceData,
        {
          id: InstrumentsPagerAction.Inactive,
          name: this.translationService.translate('Global__Disable'),
          type: ApGridPagerType.None,
          values: null
        } as IDynGridWizardChoiceData,
        {
          id: InstrumentsPagerAction.Deleted,
          name: this.translationService.translate('Global__Delete'),
          type: ApGridPagerType.None,
          values: null
        } as IDynGridWizardChoiceData
      ],
      apply: this._applyClicked
    });
  }

  private onPagerApplyClicked(data: IGridPagerData): void {
    const selectedItems = this.dynComponent.getSelectedItems() as IInstrumentGridData[];
    switch (data.object.id) {
      case InstrumentsPagerAction.Inactive:
      case InstrumentsPagerAction.Active:
        selectedItems.forEach((selectedItem: IInstrumentGridData) => {
          this.onEnableDisableToggle(selectedItem, data.object.id === InstrumentsPagerAction.Inactive);
        });
        break;
      case InstrumentsPagerAction.Deleted:
        selectedItems.forEach((selectedItem: IInstrumentGridData) => {
          this.onDelete(selectedItem);
        });
        break;
    }
  }

  private onEnableDisableToggle(dataItem: IInstrumentGridData, disable?: boolean): void {
    if (disable === undefined) {
      disable = dataItem.Status.state === ApColumnState.Green;
    }
    const item = this.instrumentStore.Instruments.find(i => i.Id === dataItem.Id);
    const copiedObject = ObjectFactory.Copy(item);
    if (item && !this.isPresentInMachines(dataItem)) {
      copiedObject.IsDeleted = disable;
      copiedObject.DeletedAt = disable ? this.dateService.getUtc() : undefined;
      copiedObject.DeletedBy = disable ? this.loginStore.User.Id : undefined;
      this.instrumentStore.updateInstrument(copiedObject);
    }
  }

  private onDelete(dataItem: IInstrumentGridData): void {
    const item = this.instrumentStore.Instruments.find(i => i.Id === dataItem.Id);
    if (item && !this.isPresentInMachines(dataItem)) {
      this.instrumentStore.deleteInstrument(item);
    }
  }

  private onEdit(dataItem: IInstrumentGridData): void {
    this.formStore.openForm({component: ApInstrumentsEditComponent, data: dataItem.SourceItem});
  }

  private isPresentInMachines(item: IInstrumentGridData): boolean {
    return this.machineStore.Machines.some(x => x.DefaultInstrument?.Id === item.Id);
  }
}
